import { useQuery } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type {
  CompanyDetail,
  RecordedOrder,
  RecordedQuote,
  TimelineEntry,
} from "../../lib/crm-types";
import type { ComposerMode } from "./ActivityComposer";

/**
 * Quotes and orders, read from where they are recorded.
 *
 * These used to have two sources and a badge saying which one was showing: Team
 * when it was connected, because Team raises the paperwork, and what reps had
 * written down here until then. **There is no API into Team and there will not
 * be one**, so the second source was the only one, and the panels read the
 * `quote` and `order` activities on the company's own timeline.
 *
 * ## Why that is not a step down from "real Team records"
 *
 * It is the same information, entered by the person who has it. A quote sitting
 * unanswered for three weeks is the single most actionable thing on an account
 * -- it is half of what the chase list ranks on -- and the desk knowing it is
 * what makes it actionable, not where the number was typed.
 *
 * ## And why they are not a second source of truth
 *
 * A recorded quote is a `crm_activity`, not a row in a quotes table. That is
 * what makes this work rather than merely function: there is no rival table to
 * reconcile, the entries already sit on the timeline with the rest of the
 * account's story, and the account manager dashboard sums the very same rows
 * for spend. If Team ever starts pushing quotes and orders in -- the way the
 * phone system pushes calls -- they arrive as the same activity type carrying
 * an `external_id`, and nothing here changes.
 *
 * Their own requests rather than fields on the company detail, which is now a
 * habit rather than a necessity: these were two calls to somebody else's server
 * and had to be able to fail on their own. Keeping them separate still earns
 * its keep -- the company page renders without waiting on either -- so they
 * have been left as they are.
 */
export function CommercialPanels({
  company,
  onCompose,
}: {
  company: CompanyDetail;
  /** Opens the timeline composer on a mode. Held by the page, so the header's
      quick actions and these buttons are one control. */
  onCompose: (mode: ComposerMode) => void;
}) {
  const linked = company.external !== null;

  const quotes = useQuery({
    queryKey: queryKeys.crmQuotes(company.id),
    queryFn: () => crmApi.quotes(company.id),
    enabled: linked,
    staleTime: 60_000,
  });

  const orders = useQuery({
    queryKey: queryKeys.crmOrders(company.id),
    queryFn: () => crmApi.orders(company.id),
    enabled: linked,
    staleTime: 60_000,
  });

  /*
   * The same quotes and orders, read off the timeline.
   *
   * This is the fallback for a company nobody has linked yet: the `/quotes` and
   * `/orders` routes answer `not_linked` for one, and a recorded quote is worth
   * showing whether or not somebody has typed an account number into Details.
   *
   * Both paths end at the same `crm_activities` rows -- the routes read them
   * typed, this reads them off the timeline -- so a linked company fetches them
   * twice. That is worth collapsing; it is left alone here because choosing
   * which of the two feeds the panel changes what a client sees (the routes cap
   * at 20 and answer `not_linked`, the timeline caps at 50 and never does), and
   * that is a product decision rather than a tidy-up.
   *
   * Its key is the panels' own and not the timeline's -- see
   * `crmCommercialPanels`, which records why sharing one was a white screen.
   */
  const recorded = useQuery({
    queryKey: queryKeys.crmCommercialPanels(company.id),
    queryFn: () =>
      crmApi.timeline(company.id, { kinds: ["quote", "order"], limit: 50 }),
    staleTime: 30_000,
  });

  const entries = recorded.data?.entries ?? [];
  const recordedQuotes = entries.filter((entry) => entry.kind === "quote");
  const recordedOrders = entries.filter((entry) => entry.kind === "order");

  return (
    <>
      <Panel
        title="Quotes"
        addLabel="Add quote"
        onAdd={() => onCompose("quote")}
        loading={linked && quotes.isLoading}
        rows={
          linked && quotes.data?.available
            ? quotes.data.items.map(fromRecordedQuote)
            : recordedQuotes.map(fromRecorded)
        }
        empty="No quotes recorded yet."
      />

      <Panel
        title="Orders"
        addLabel="Add order"
        onAdd={() => onCompose("order")}
        loading={linked && orders.isLoading}
        rows={
          linked && orders.data?.available
            ? orders.data.items.map(fromRecordedOrder)
            : recordedOrders.map(fromRecorded)
        }
        empty="No orders recorded yet."
      />

      <TeamNotice company={company} />
    </>
  );
}

/** One line in a panel, whichever side it came from. */
interface Row {
  id: string;
  primary: string;
  secondary: string;
  url: string | null;
}

function fromRecordedQuote(quote: RecordedQuote): Row {
  return {
    id: String(quote.id),
    primary: quote.reference,
    secondary: [quote.status, money(quote.netTotal)].filter(Boolean).join(" · "),
    /* No deep link per quote: these are our own records of somebody else's
       paperwork, and we have no URL for a quote we did not raise. */
    url: null,
  };
}

function fromRecordedOrder(order: RecordedOrder): Row {
  return {
    id: String(order.id),
    primary: order.reference,
    secondary: money(order.netTotal) ?? "",
    url: null,
  };
}

/**
 * A hand-recorded one.
 *
 * The reference falls back to the subject because the subject is built from it
 * (`Quote 41208`), so a row is readable even if an older entry predates the
 * structured field.
 */
function fromRecorded(entry: TimelineEntry): Row {
  const data = entry.data ?? {};
  const reference =
    typeof data.reference === "string" && data.reference
      ? data.reference
      : entry.subject ?? "—";

  const outcome = typeof data.outcome === "string" ? data.outcome : null;
  const net = typeof data.netTotal === "number" ? data.netTotal : null;

  return {
    id: entry.id,
    primary: reference,
    secondary: [outcome, money(net), when(entry.occurredAt)]
      .filter(Boolean)
      .join(" · "),
    url: null,
  };
}

/** Net of VAT, matching how Team reports everywhere else. */
function money(value: number | null): string {
  if (value === null) return "";
  return `£${value.toLocaleString(undefined, { minimumFractionDigits: 2 })} net`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function Panel({
  title,
  rows,
  loading,
  empty,
  addLabel,
  onAdd,
}: {
  title: string;
  rows: Row[];
  loading: boolean;
  empty: string;
  addLabel: string;
  onAdd: () => void;
}) {
  return (
    <section className="rounded-lg border border-line">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          {title}
        </h2>
        {rows.length > 0 ? (
          <span className="text-[11px] text-faint">{rows.length}</span>
        ) : null}
        <button
          type="button"
          onClick={onAdd}
          className="ml-auto rounded-md border border-line px-2 py-0.5 text-[11px] text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
        >
          {addLabel}
        </button>
      </header>

      {loading ? (
        <p className="px-3 py-3 text-[11px] text-faint">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-3 py-3 text-[11px] text-faint">{empty}</p>
      ) : (
        <ul className="max-h-64 divide-y divide-line overflow-y-auto">
          {rows.map((row) => (
            <li key={row.id} className="px-3 py-1.5 text-xs">
              {row.url ? (
                <a
                  href={row.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-content hover:text-brand-600"
                >
                  {row.primary}
                </a>
              ) : (
                <span className="text-content">{row.primary}</span>
              )}
              <div className="text-[11px] text-faint">{row.secondary}</div>
            </li>
          ))}
        </ul>
      )}

      {/* Said on the panel rather than only in the docs: a figure nobody can
          trace is a figure nobody trusts, and a rep should know that "£40k of
          quotes" is what the desk has written down rather than what somebody
          else's ledger says.

          This used to read "From Team." for a linked company, which was false
          in the only case it ever appeared: both branches show the same
          `crm_activities` rows, so it credited Team with figures a rep had
          typed in. */}
      <footer className="border-t border-line px-3 py-1.5 text-[10px] text-faint">
        Recorded here, on the timeline.
      </footer>
    </section>
  );
}

/**
 * Why the panels above are reading from notes.
 *
 * Its own strip rather than the body of an empty panel, which is what it used
 * to be: the panels now have rows in them, and an explanation that used to be
 * the only content would otherwise be repeated twice above real data.
 */
function TeamNotice({ company }: { company: CompanyDetail }) {
  /*
   * One state left, where there used to be two.
   *
   * The other was "Team has no API configured", which every company showed at
   * once and which an admin was told to fix with two environment variables.
   * There is no API into Team and there is not going to be one -- it writes to
   * Pulse -- so the only reason a panel has nothing to belong to is that nobody
   * has linked this company to an account yet, which is a job for a person on
   * this page rather than a deployment change.
   */
  if (company.external !== null) return null;

  return (
    <section className="rounded-lg border border-dashed border-line px-3 py-2">
      <p className="text-[11px] text-faint">
        This company is not linked to an account in the fulfilment platform. Add
        their ID and anything recorded against them will appear above.
      </p>
    </section>
  );
}
